import { cn } from "@/utils/cn";
import { EditorContent } from "@tiptap/react";
import { useMemo, useState } from "react";
import { graphql, useFragment } from "react-relay";
import { Button } from "@/components/ui/button";
import { useRouteContext } from "@tanstack/react-router";
import { useEditor } from "@/editor/editor";
import { useMeasure } from "@uidotdev/usehooks";
import type { postContentPreviewFragment$key } from "./__generated__/postContentPreviewFragment.graphql";

export function PostContentPreview({
	post,
}: {
	post: postContentPreviewFragment$key;
}) {
	const [editorRef, editorMeasure] = useMeasure();
	const [containerRef, containerMeasure] = useMeasure();

	const { club, session } = useRouteContext({ from: "__root__" });

	const [showContent, setShowContent] = useState(false);

	const data = useFragment<postContentPreviewFragment$key>(
		graphql`
    fragment postContentPreviewFragment on Post {
      content
    }
  `,
		post,
	);

	const editor = useEditor({
		editable: false,
		content: data.content,
		features: {
			placeholder: { enabled: true },
			colors: { enabled: true },
			attachments: { enabled: true },
			video: { enabled: true },
			poll: { enabled: true },
			youtube: { enabled: true },
			image: { enabled: true },
			iframe: { enabled: true },
			button: { enabled: true },
			mention: { enabled: true },
			styling: { enabled: true },
		},
		club: club?.id,
		session: session,
	});

	const isScrollable = useMemo(() => {
		if (showContent) {
			return true;
		}

		return (editorMeasure?.height || 0) > (containerMeasure?.height || 0);
	}, [editorMeasure, containerMeasure, showContent]);

	return (
		<div
			className={cn(
				"overflow-y-hidden relative w-full flex-1",
				!showContent ? "max-h-72" : "h-fit",
			)}
			ref={containerRef}
		>
			<EditorContent editor={editor!} ref={editorRef} />

			{isScrollable && showContent ? (
				<div className="bg-card w-full mt-6">
					<Button
						type="button"
						variant="ghost"
						onClick={() => setShowContent(!showContent)}
					>
						{showContent ? "Ver Menos" : "Ver Mais"}
					</Button>
				</div>
			) : isScrollable ? (
				<div className="absolute bottom-0 w-full">
					<div className="w-full bg-linear-to-t from-card to-transparent h-16" />
					<div className="bg-card w-full pt-2">
						<Button
							type="button"
							variant="ghost"
							onClick={() => setShowContent(!showContent)}
						>
							{showContent ? "Ver Menos" : "Ver Mais"}
						</Button>
					</div>
				</div>
			) : null}
		</div>
	);
}
